import { useEffect, useState } from "react";

import { iniciarGeneracion } from "../api/client";
import type { Proyecto } from "../api/types";
import { useProyecto } from "../hooks/useProyecto";
import { BadgeEstado } from "./BadgeEstado";
import { BarraPresupuesto } from "./BarraPresupuesto";
import { EditorVisualFlyer } from "./EditorVisualFlyer";
import { PanelFormatos } from "./PanelFormatos";
import { PanelTextoLiteral } from "./PanelTextoLiteral";
import { ProgresoGeneracion } from "./ProgresoGeneracion";
import { SeccionMarca } from "./SeccionMarca";
import estilos from "./VistaProyecto.module.css";

interface Props {
  token: string;
  proyectoId: string;
  onProyectoActualizado?: (proyecto: Proyecto) => void;
}

// Panel del proyecto seleccionado: brief + ajustes elegidos, boton
// "Generar" (POST /generate), barra de progreso por sondeo mientras
// estado === "generando" (useProyecto), y visor del PNG cuando esta listo.
export function VistaProyecto({
  token,
  proyectoId,
  onProyectoActualizado,
}: Props): JSX.Element {
  const { proyecto, cargando, error, refrescar } = useProyecto(
    token,
    proyectoId,
  );
  const [generando, setGenerando] = useState(false);
  const [errorGeneracion, setErrorGeneracion] = useState<string | null>(null);
  const [editorAbierto, setEditorAbierto] = useState(false);

  useEffect(() => {
    if (proyecto && onProyectoActualizado) onProyectoActualizado(proyecto);
  }, [proyecto, onProyectoActualizado]);

  const generar = async (): Promise<void> => {
    setGenerando(true);
    setErrorGeneracion(null);
    try {
      await iniciarGeneracion(token, proyectoId);
      // Trae el proyecto de inmediato (probablemente ya "generando") en vez
      // de esperar el primer tick del sondeo de useProyecto.
      await refrescar();
    } catch (err) {
      setErrorGeneracion(
        err instanceof Error ? err.message : "No se pudo iniciar la generacion",
      );
    } finally {
      setGenerando(false);
    }
  };

  if (cargando && !proyecto) {
    return (
      <section className={estilos.tarjeta}>
        <div className={estilos.estadoVacio}>Cargando proyecto...</div>
      </section>
    );
  }

  if (error && !proyecto) {
    return (
      <section className={estilos.tarjeta}>
        <div className={estilos.errorPanel}>
          <h3>No se pudo cargar el proyecto</h3>
          <p>{error}</p>
          <button
            type="button"
            className="boton boton-secundario"
            onClick={() => void refrescar()}
          >
            Reintentar
          </button>
        </div>
      </section>
    );
  }

  if (!proyecto) return <></>;

  const puedeGenerar = proyecto.estado !== "generando" && !generando;
  const etiquetaBoton =
    proyecto.estado === "draft" ? "Generar" : "Generar de nuevo";
  const puedeEditar =
    proyecto.flyer_document !== null && proyecto.estado !== "generando";

  const alGuardarEnEditor = (actualizado: Proyecto): void => {
    onProyectoActualizado?.(actualizado);
    void refrescar();
  };

  return (
    <section className={estilos.tarjeta} aria-labelledby="titulo-proyecto">
      <header className={estilos.encabezado}>
        <div>
          <p id="titulo-proyecto" className={estilos.brief}>
            {proyecto.brief}
          </p>
          <div className={estilos.meta}>
            <span className={estilos.metaDato}>Formato: {proyecto.format}</span>
            {proyecto.model && (
              <span className={estilos.metaDato}>Modelo: {proyecto.model}</span>
            )}
            {proyecto.effort && (
              <span className={estilos.metaDato}>
                Esfuerzo: {proyecto.effort}
              </span>
            )}
          </div>
        </div>
        <div className={estilos.encabezadoDerecha}>
          <BadgeEstado estado={proyecto.estado} />
          <BarraPresupuesto
            gastadoUsd={proyecto.gastado_usd}
            presupuestoUsd={proyecto.budget_max_usd}
            remanenteUsd={proyecto.remanente_usd}
          />
        </div>
      </header>

      <details className={estilos.seccionAssets}>
        <summary className={estilos.seccionAssetsResumen}>
          Texto literal
        </summary>
        <div className={estilos.seccionAssetsCuerpo}>
          <PanelTextoLiteral
            token={token}
            proyecto={proyecto}
            deshabilitado={proyecto.estado === "generando"}
            onGuardado={(actualizado) => {
              onProyectoActualizado?.(actualizado);
              void refrescar();
            }}
          />
        </div>
      </details>

      <details className={estilos.seccionAssets}>
        <summary className={estilos.seccionAssetsResumen}>
          Assets y kit de marca
        </summary>
        <div className={estilos.seccionAssetsCuerpo}>
          <SeccionMarca token={token} proyectoId={proyecto.id} />
        </div>
      </details>

      <ProgresoGeneracion
        estado={proyecto.estado}
        progreso={proyecto.progress}
      />

      {proyecto.estado === "cuota_agotada" && (
        <div className={estilos.avisoCuota} role="status">
          <h3>Cuota agotada</h3>
          <p>
            Se agoto la cuota de la suscripcion de Claude compartida por el
            equipo; reintenta mas tarde.
          </p>
        </div>
      )}

      {proyecto.estado === "budget_exceeded" && (
        <div className={estilos.avisoCuota} role="status">
          <h3>Presupuesto agotado</h3>
          <p>Se alcanzo el tope de presupuesto del proyecto.</p>
        </div>
      )}

      {proyecto.estado === "fallido" && (
        <div className={estilos.errorPanel} role="alert">
          <h3>La generacion termino con error</h3>
          <p>
            {proyecto.error?.message ??
              "Ocurrio un error durante la generacion."}
          </p>
        </div>
      )}

      {errorGeneracion && (
        <div className={estilos.errorPanel} role="alert">
          <p>{errorGeneracion}</p>
        </div>
      )}

      {proyecto.estado === "listo" && proyecto.png_url && (
        <div className={estilos.seccion}>
          <div className={estilos.visor}>
            <img
              src={proyecto.png_url}
              alt={`Flyer generado a partir del brief: ${proyecto.brief}`}
              className={estilos.imagen}
            />
            <a
              className="boton boton-secundario"
              href={proyecto.png_url}
              target="_blank"
              rel="noreferrer"
            >
              Abrir imagen en pestana nueva
            </a>
          </div>
        </div>
      )}

      <PanelFormatos
        token={token}
        proyecto={proyecto}
        onCambioPresupuesto={() => void refrescar()}
      />

      <div className={estilos.cta}>
        {puedeEditar && (
          <button
            type="button"
            className="boton boton-secundario boton-grande"
            onClick={() => setEditorAbierto(true)}
          >
            Editar flyer
          </button>
        )}
        <button
          type="button"
          className="boton boton-primario boton-grande"
          onClick={() => void generar()}
          disabled={!puedeGenerar}
        >
          {generando ? "Encolando..." : etiquetaBoton}
        </button>
      </div>

      {editorAbierto && (
        <EditorVisualFlyer
          token={token}
          proyecto={proyecto}
          onGuardado={alGuardarEnEditor}
          onCerrar={() => setEditorAbierto(false)}
        />
      )}
    </section>
  );
}
